import { saveApplovinLanding } from '@openeducation/pp-js-utils';
import { pmCountryAtom } from '@openeducation/pp-rn-jotai-atoms';
import {
  BillingFrequency,
  type CreditCard,
  type Currency,
  type FreeTrialsPaymentInfo,
  type ManualPaymentBanks,
  PaymentMethod,
} from '@openeducation/pp-rn-shared-types';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';

import { useContractInfoProps } from './use-contract-info-props';
import { useGetCheckoutScreenData } from './use-get-checkout-screen-data';
import { useInvoiceCardProps } from './use-invoice-card-props';
import { useManualPaymentInfoProps } from './use-manual-payment-info-props';
import { useNextInvoice } from './use-next-invoice';
import { usePaymentSubmitProps } from './use-payment-submit-props';
import { useHandleAddressValidation } from './use-payment-submit-props/use-handle-address-validation';
import type { SubmitPayPalParams } from './use-submit-pay-pal';
import { useTrackBeginCheckout } from './use-track-begin-checkout';
import {
  useGoogleTagManager,
  useSubmitPayment as useSubmitPaymentCall,
} from '../../../hooks';
import {
  getBillingAddress,
  getIsSubmitEnabledParams,
  getSubmitPayUParams,
  getManualPaymentParams,
} from '../../helpers';
import { useAdditionalPaymentInfoProps } from '../use-additional-payment-info-props';
import { useErrorModalProps } from '../use-error-modal-props';
import { useIsSubmitEnabled } from '../use-is-submit-enabled';
import { usePageTemplateProps } from '../use-page-template-props';
import { usePaymentInfoProps } from '../use-payment-info-props';
import { usePaymentStatusModalProps } from '../use-payment-status-modal-props';
import { usePersonalInfoProps } from '../use-personal-info-props';
import type { SubmitYunoParams } from '../use-submit-yuno';

export const useCheckoutScreen = (sfdcToken: string, baseUrl: string) => {
  const {
    isScreenLoading,
    hasScreenDataError,
    data,
    contactPhone,
    organization,
    isB2B,
    isUpsell,
    countryISOCode,
  } = useGetCheckoutScreenData({ sfdcToken });
  const {
    tinyPayloads,
    personalDetails,
    commonPaymentInfo,
    allCountries,
    creditCardPaymentInfo,
    alternativePaymentInfo,
    additionalPaymentInfo,
    buyer,
    company,
    subscription,
    webCheckoutFormData,
    countryStates,
    freeTrialsInfo = {} as FreeTrialsPaymentInfo,
    manualPaymentInfo,
    billingAgreements,
    creditCardNumber,
    yunoPaymentStatus,
    manualPaymentBanks = {} as ManualPaymentBanks,
    nextInvoice,
  } = data;

  const paymentFlowType = tinyPayloads?.paymentFlowType;
  const customerEmailAddress =
    buyer?.emailAddress || subscription?.emailAddress || '';
  const productDescription = tinyPayloads?.ratePlan?.name || '';
  const pmCountry = useAtomValue(pmCountryAtom);
  const { hasFreeTrial, trialEndDateEpoch } = freeTrialsInfo;
  const { trackBeginCheckout } = useTrackBeginCheckout();
  const { pushEvent } = useGoogleTagManager();
  const hasTrackedBeginCheckout = useRef(false);

  const isFinanced = tinyPayloads?.billingFrequency === BillingFrequency.FINANCED;

  const { pageTemplateProps } = usePageTemplateProps({
    isScreenLoading,
    contactPhone,
    organization,
    isB2B,
    isFinanced,
  });

  const { nextInvoiceProps } = useNextInvoice(nextInvoice);

  const { invoiceCardProps } = useInvoiceCardProps({
    tinyPayloads,
    freeTrialsInfo,
  });

  const fullName = useMemo(() => {
    if (buyer) {
      return `${buyer.firstName} ${buyer.lastName}`;
    }
    if (subscription) {
      return `${subscription.firstName} ${subscription.lastName}`;
    }
    return '';
  }, [buyer, subscription]);

  const { personalInfoProps } = usePersonalInfoProps({
    fullName,
    email: buyer?.emailAddress || subscription?.emailAddress || '',
    personalDetails,
    countryISOCode,
  });

  const { paymentInfoProps } = usePaymentInfoProps({
    countryISOCode,
    commonPaymentInfo,
    allCountries,
    creditCardPaymentInfo,
    tinyPayloads,
    subscription,
    creditCardNumber,
    splitInvoice: invoiceCardProps?.isFirstPayment,
  });

  const isTwoCreditCards = paymentInfoProps.formData.paymentMethod === PaymentMethod.TWO_CREDIT_CARDS;

  const { additionalPaymentInfoProps: card1AdditionalPaymentInfoProps } = useAdditionalPaymentInfoProps({
    additionalPaymentInfo,
    isExistingPaymentMethod: paymentInfoProps.fields.isExistingPaymentMethod,
    countryStates,
    cardIndex: 1,
    onGlobalFieldChange: paymentInfoProps.onFieldChange,
  });

  const { additionalPaymentInfoProps: card2AdditionalPaymentInfoProps } = useAdditionalPaymentInfoProps({
    additionalPaymentInfo,
    isExistingPaymentMethod: paymentInfoProps.fields.isExistingPaymentMethod,
    countryStates,
    cardIndex: 2,
    onGlobalFieldChange: paymentInfoProps.onFieldChange,
  });

  const { contractInfoProps, contractParams } = useContractInfoProps({
    customerEmailAddress,
    creditCardNumber: paymentInfoProps.validation.isCard1NumberValid
      ? paymentInfoProps.formData.card1_cardNumber
      : '',
    creditCardType: paymentInfoProps.validation.isCard1TypeValid
      ? paymentInfoProps.formData.card1_cardType
      : ('' as CreditCard),
    tinyPayloads,
    company,
    freeTrial: {
      hasFreeTrial,
      trialEndDateEpoch,
    },
    nextInvoice,
    creditCardNumber2: isTwoCreditCards && paymentInfoProps.validation.isCard2NumberValid
      ? paymentInfoProps.formData.card2_cardNumber
      : undefined,
    creditCardType2: isTwoCreditCards && paymentInfoProps.validation.isCard2TypeValid
      ? paymentInfoProps.formData.card2_cardType
      : undefined,
  });

  const billingAddress1 = getBillingAddress({
    additionalPaymentInfo: card1AdditionalPaymentInfoProps.formData,
    pmCountry,
  });

  const billingAddress2 = getBillingAddress({
    additionalPaymentInfo: card2AdditionalPaymentInfoProps.formData,
    pmCountry,
  });

  const { manualPaymentInfoProps } = useManualPaymentInfoProps({
    manualPaymentInfo,
    manualPaymentBanks,
  });

  const manualPaymentParams = getManualPaymentParams(
    manualPaymentInfoProps.formData
  );

  const isSubmitEnabledParams = getIsSubmitEnabledParams({
    paymentInfoProps,
    additionalPaymentInfoProps: card1AdditionalPaymentInfoProps,
    additionalPaymentInfo2Props: card2AdditionalPaymentInfoProps,
    contractInfoProps,
    manualPaymentInfoProps,
  });
  const { isSubmitEnabled } = useIsSubmitEnabled(isSubmitEnabledParams);

  const submitPayPalParams: SubmitPayPalParams = {
    baseUrl,
    agreementDescription: productDescription,
    pmCountry,
    sfdcToken,
    zuoraAccountId: null,
  };

  const submitPayUParams = getSubmitPayUParams({
    baseUrl,
    webCheckoutFormData,
    buyerEmail: customerEmailAddress,
    productDescription,
    amount: invoiceCardProps.total,
    extra1: organization as string,
    currency: tinyPayloads?.currencyISOCode as Currency,
  });

  const submitYunoParams: SubmitYunoParams = {
    baseUrl,
    pmCountry,
    sfdcToken,
    documentNumber:
      card1AdditionalPaymentInfoProps.formData.documentNumber ||
      card2AdditionalPaymentInfoProps.formData.documentNumber,
  };

  const submitPaymentReturn = useSubmitPaymentCall({
    paymentFlowType,
  });

  const { addressValidationModalProps } = useHandleAddressValidation({
    billingAddress: billingAddress1,
  });

  const { addressValidationModalProps: addressValidationModalProps2 } = useHandleAddressValidation({
    billingAddress: billingAddress2,
  });

  const { paymentSubmitProps } = usePaymentSubmitProps({
    alternativePaymentInfo,
    paymentInfo: paymentInfoProps.formData,
    documentNumber: card1AdditionalPaymentInfoProps.formData.documentNumber,
    documentType: card1AdditionalPaymentInfoProps.formData.documentType,
    documentNumber2: card2AdditionalPaymentInfoProps.formData.documentNumber,
    documentType2: card2AdditionalPaymentInfoProps.formData.documentType,
    isUpsell,
    contractParams,
    sfdcToken,
    isSubmitEnabled,
    submitPayPalParams,
    submitPayUParams,
    submitYunoParams,
    manualPaymentParams,
    submitPaymentReturn,
    isScreenLoading,
    billingAgreements,
    billingAddress: addressValidationModalProps.address,
    billingAddress2: addressValidationModalProps2.address,
    validateAddress: addressValidationModalProps.validateAddress,
    validateAddress2: addressValidationModalProps2.validateAddress,
    isAddressValid: addressValidationModalProps.isAddressValid,
    isAddressValid2: addressValidationModalProps2.isAddressValid,
  });

  const { errorModalProps } = useErrorModalProps({
    contactPhone,
    isB2B,
  });

  const { paymentStatusModalProps } = usePaymentStatusModalProps({
    paymentFlowType,
    alternativePaymentGateway: alternativePaymentInfo?.paymentGateway,
    // Only the checkout screen pushes the purchase event to the dataLayer.
    onPurchase: () => pushEvent({ event: 'purchase' }),
  });

  useEffect(() => {
    if (sfdcToken) {
      saveApplovinLanding(sfdcToken);
    }
  }, [sfdcToken]);

  // Once per page load, when the screen data is loaded. It does not need a
  // buyer (upsells have none). It does not fire when the tiny payload, buyer or
  // subscription call fails: the checkout shows an error and the user cannot
  // pay.
  useEffect(() => {
    if (
      hasTrackedBeginCheckout.current ||
      !tinyPayloads ||
      isScreenLoading ||
      hasScreenDataError
    ) {
      return;
    }
    hasTrackedBeginCheckout.current = true;
    trackBeginCheckout({ sfdcToken });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tinyPayloads, isScreenLoading, hasScreenDataError]);

  return {
    pageTemplateProps,
    invoiceCardProps,
    personalInfoProps,
    paymentInfoProps: {
      ...paymentInfoProps,
      additionalPaymentInfoCard1: card1AdditionalPaymentInfoProps,
      additionalPaymentInfoCard2: card2AdditionalPaymentInfoProps,
    },
    contractInfoProps,
    paymentSubmitProps,
    contactPhone,
    errorModalProps,
    paymentStatusModalProps,
    manualPaymentInfoProps,
    addressValidationModalProps,
    yunoPaymentStatus,
    nextInvoiceProps
  };
};
